import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { k8sResourceService } from '@/services/k8sResourceService'
import { projectService } from '@/services/projectService'
import { authService } from '@/services/authService'
import ProjectLayout from '@/components/ProjectLayout'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import ErrorDisplay from '@/components/ui/ErrorDisplay'
import CustomizableTable, { type TableColumn } from '@/components/ui/CustomizableTable'
import { ExternalLink, StatusLabel, type StatusType } from '@/components/ui/labels'
import { HttpError } from '@/lib/httpError'
import DeleteFlowModal, { type DeleteFlowModalRef } from '@/components/modals/DeleteFlowModal'
import RestartFlowModal, { type RestartFlowModalRef } from '@/components/modals/RestartFlowModal'

// Get External Access URL (using externalIP + port)
const getExternalAccessUrl = (flow: any) => {
  if (!flow.status?.externalIP || !flow.status?.port) return null
  const token = authService.getAccessToken()
  const baseUrl = `http://${flow.status.externalIP}:${flow.status.port}`
  return token ? `${baseUrl}?access_token=${token}` : baseUrl
}

// Get NodePort Access URL (using random node IP + nodePort)
const getNodePortUrl = (flow: any, nodeIPs: string[]) => {
  if (!flow.status?.nodePort || !nodeIPs.length) return null
  // Randomly select a node IP
  const randomNodeIP = nodeIPs[Math.floor(Math.random() * nodeIPs.length)]
  const token = authService.getAccessToken()
  const baseUrl = `http://${randomNodeIP}:${flow.status.nodePort}`
  return token ? `${baseUrl}?access_token=${token}` : baseUrl
}

// Get Ingress URL (protocol based on TLS)
const getIngressUrl = (flow: any) => {
  if (!flow.status?.ingressHost) return null
  const protocol = flow.status.tls ? 'https' : 'http'
  const port = ''
  const token = authService.getAccessToken()
  const baseUrl = `${protocol}://${flow.status.ingressHost}${port}`
  return token ? `${baseUrl}?access_token=${token}` : baseUrl
}

export default function ProjectPipelineStudioPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { t, i18n } = useTranslation(['pipeline-studio', 'common'])
  const { user, refreshAuth } = useAuth()
  const navigate = useNavigate()
  const deleteModalRef = useRef<DeleteFlowModalRef>(null)
  const restartModalRef = useRef<RestartFlowModalRef>(null)
  const [project, setProject] = useState<any>(null)
  const [flows, setFlows] = useState<any[]>([])
  const [nodeIPs, setNodeIPs] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState('')
  const [continueToken, setContinueToken] = useState<string | undefined>(undefined)

  // Check permissions
  const isPlatformOwner = user?.platform_role === 'owner'
  const isProjectOwner = project?.role === 'owner'
  const isProjectAdmin = project?.role === 'admin'
  const canManageFlows = isPlatformOwner || isProjectOwner || isProjectAdmin
  const canDirectDelete = isPlatformOwner || isProjectOwner
  const canRequestDelete = isPlatformOwner || isProjectOwner || isProjectAdmin

  // Fetch project info to get project name
  useEffect(() => {
    const fetchProjectInfo = async () => {
      if (!projectId) return

      try {
        const data = await projectService.getProjectById(projectId)
        setProject(data)
      } catch (err) {
        if (err instanceof HttpError && err.status === 401) {
          try {
            await refreshAuth()
            const data = await projectService.getProjectById(projectId)
            setProject(data)
          } catch (refreshError) {
            console.error('Failed to fetch project info:', refreshError)
          }
        }
      }
    }

    fetchProjectInfo()
  }, [projectId, refreshAuth])

  useEffect(() => {
    if (projectId && project) {
      fetchData()
    }
  }, [projectId, project])

  const fetchData = async (reset = true) => {
    if (!projectId || !project) return

    if (reset) {
      setLoading(true)
      setContinueToken(undefined)
    } else {
      setLoadingMore(true)
    }
    setError('')
    const startTime = Date.now()

    try {
      // Fetch flows for this project (namespace = project.name) and nodes in parallel
      const [flowsData, nodesData] = await Promise.all([
        k8sResourceService.getFlows(project.name, reset ? undefined : continueToken),
        reset ? k8sResourceService.getNodes() : Promise.resolve({ items: [], metadata: {}, apiVersion: 'v1', kind: 'NodeList' })
      ])

      // Extract node IPs (only on first load)
      if (reset) {
        const ips = k8sResourceService.extractNodeIPs(nodesData)
        setNodeIPs(ips)
        setFlows(flowsData.items || [])
      } else {
        // Append new flows to existing list
        setFlows(prev => [...prev, ...(flowsData.items || [])])
      }

      // Save continue token for next page
      setContinueToken(flowsData.metadata?.continue)
      setError('')

      // Ensure minimum 2 seconds loading time
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }
    } catch (err) {
      // Handle 401 error: try to refresh token
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          // Retry fetching data after successful refresh
          return fetchData()
        } catch (refreshError) {
          // Refresh failed, ensure at least 2 seconds have passed
          const elapsed = Date.now() - startTime
          const remainingTime = Math.max(0, 2000 - elapsed)
          if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime))
          }
          const refreshErrorMessage = refreshError instanceof Error ? refreshError.message : 'errors.sessionExpired'
          if (refreshErrorMessage.startsWith('errors.')) {
            setError(t(refreshErrorMessage, { ns: 'common', defaultValue: refreshErrorMessage }))
          } else {
            setError(refreshErrorMessage)
          }
          setLoading(false)
          return
        }
      }

      // For other errors, ensure at least 2 seconds have passed
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, { ns: 'common', defaultValue: errorMessage }))
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const loadMore = () => {
    if (continueToken && !loadingMore) {
      fetchData(false)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      const locale = i18n.language === 'zh' ? 'zh-TW' : 'en-US'

      const formattedDate = date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })

      return formattedDate
    } catch {
      return dateString
    }
  }

  const getAuthor = (item: any) => {
    return item.metadata?.annotations?.['synstream.console/author'] || '-'
  }

  const tableColumns: TableColumn[] = [
    {
      id: 'name',
      label: t('flow.name', 'Name'),
      accessor: 'metadata.name',
      sortable: true,
      alwaysVisible: true,
      render: (value: string, item: any) => (
        <div
          onClick={() => navigate(`/projects/${projectId}/products/pipeline-studio/${item.metadata?.namespace}/${value}`)}
          className="cursor-pointer hover:text-primary transition-colors"
        >
          <div className="font-semibold text-text-dark group-hover:text-primary">{value}</div>
          <div className="text-sm text-gray-500">{item.metadata?.namespace}</div>
        </div>
      )
    },
    {
      id: 'status',
      label: t('flow.status', 'Status'),
      accessor: 'status.phase',
      sortable: true,
      filterable: true,
      filterOptions: ['Ready', 'Progressing', 'Error'],
      render: (value: string) => (
        <StatusLabel status={value as StatusType} size="sm" />
      )
    },
    {
      id: 'author',
      label: t('flow.author', 'Author'),
      accessor: (item: any) => getAuthor(item),
      sortable: true,
      render: (value: string) => (
        <div className="text-sm">
          <div className="font-medium text-gray-900">{value}</div>
        </div>
      )
    },
    {
      id: 'created',
      label: t('flow.created', 'Created'),
      accessor: 'metadata.creationTimestamp',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm">
          <div className="font-medium text-gray-900">{formatDate(value)}</div>
        </div>
      )
    },
    {
      id: 'externalAccess',
      label: t('flow.externalAccess', 'External Access'),
      accessor: (item: any) => getExternalAccessUrl(item),
      defaultVisible: false,
      render: (value: string | null, item: any) => (
        <ExternalLink href={value} variant="success" size="sm">
          {item.status?.externalIP || '-'}
        </ExternalLink>
      )
    },
    {
      id: 'nodePortAccess',
      label: t('flow.nodePortAccess', 'NodePort Access'),
      accessor: (item: any) => getNodePortUrl(item, nodeIPs),
      defaultVisible: false,
      render: (value: string | null, item: any) => (
        <ExternalLink href={value} variant="warning" size="sm">
          {item.status?.nodePort || '-'}
        </ExternalLink>
      )
    },
    {
      id: 'ingressAccess',
      label: t('flow.ingressAccess', 'Ingress Access'),
      accessor: (item: any) => getIngressUrl(item),
      render: (value: string | null, item: any) => (
        <ExternalLink href={value} variant="primary" size="sm">
          {item.status?.ingressHost || '-'}
        </ExternalLink>
      )
    },
    {
      id: 'actions',
      label: t('flow.actions', 'Actions'),
      accessor: () => '',
      render: (_: any, item: any) => (
        <div className="flex space-x-2">
          {canManageFlows ? (
            <>
              <button
                onClick={() => restartModalRef.current?.open(item)}
                className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
              >
                {t('flow.restart', 'Restart')}
              </button>
              <button
                onClick={() => deleteModalRef.current?.open(item)}
                className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                {t('flow.delete', 'Delete')}
              </button>
            </>
          ) : (
            <span className="text-sm text-gray-400">-</span>
          )}
        </div>
      )
    }
  ]

  return (
    <ProjectLayout>
      <div className="max-w-7xl mx-auto">
        {/* Full Screen Loading Overlay */}
        {loading && !error && <LoadingOverlay message={t('projectLoading', 'Loading flows...')} fullScreen />}

        {/* Full Screen Error Display */}
        {error && !loading && (
          <ErrorDisplay
            error={error}
            title={t('errors.title', 'Error')}
            onRefresh={() => fetchData()}
            fullScreen
          />
        )}

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center">
                <img src="/PS.png" alt="Pipeline Studio" className="w-12 h-12 rounded-lg" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-text-dark">{t('title', 'Pipeline Studio')}</h1>
                <p className="text-gray-600 mt-1">{t('projectDescription', 'Manage and monitor your data pipelines')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchData()}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{t('actions.refresh', 'Refresh')}</span>
              </button>
              {canManageFlows && (
                <button
                  onClick={() => navigate(`/projects/${projectId}/products/pipeline-studio/create`)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>{t('flow.add', 'Add Flow')}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {!loading && !error && (
          <>
            <CustomizableTable
              data={flows}
              columns={tableColumns}
              loading={false}
              emptyMessage={t('empty.noFlows', 'No flows found in this project')}
            />

            {/* Load More Button */}
            {continueToken && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingMore && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>{loadingMore ? t('actions.loading', 'Loading...') : t('actions.loadMore', 'Load More')}</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Flow Modal */}
      <DeleteFlowModal
        ref={deleteModalRef}
        canDirectDelete={canDirectDelete}
        canRequestDelete={canRequestDelete}
        onSuccess={() => fetchData()}
      />

      {/* Restart Flow Modal */}
      <RestartFlowModal
        ref={restartModalRef}
        canDirectRestart={canDirectDelete}
        canRequestRestart={canRequestDelete}
        onSuccess={() => fetchData()}
      />
    </ProjectLayout>
  )
}
